import { LinkTarget } from '@atlassian/learning-components';

import { CollectionType } from 'src/constants';
import { AtImage, Resource } from 'src/graphql/generated/graphql';
import {
  Certification,
  CertificationPrep,
  ContentHub,
  ContentItem,
  LearningPath,
  Role,
  SmCourse,
  SmLesson,
  Topic,
  AssociatedContent,
} from 'src/types/contentful/base-content';
import { ContentType } from 'src/types/contentful/content-type';
import {
  getCertificationUrl,
  getCollectionUrl,
  getCourseUrl,
  getHubUrl,
  getLessonUrl,
  getPathUrl,
} from 'src/utils/getUrl';

export const getContentDescription = (content: ContentItem): string => {
  if (content.__typename === ContentType.ASSOCIATED_CONTENT) {
    const associatedContent = content as AssociatedContent;

    return associatedContent.description || '';
  }

  if ('description' in content) {
    return content.description || '';
  }

  return '';
};

export const getContentLink = (content: ContentItem): string => {
  if (content.__typename === ContentType.ASSOCIATED_CONTENT) {
    const associatedContent = content as AssociatedContent;

    if (associatedContent.link?.link) {
      return associatedContent.link.link;
    }

    return '';
  }

  if (content.__typename === ContentType.LEARNING_PATH) {
    const learningPath = content as LearningPath;

    return getPathUrl(learningPath.slug!);
  }

  if (content.__typename === ContentType.COURSE) {
    const course = content as SmCourse;

    return getCourseUrl(course.slug!);
  }

  if (content.__typename === ContentType.LESSON) {
    const lesson = content as SmLesson;
    const course = lesson.linkedFrom?.smCourseCollection?.items[0];

    if (course) {
      return getLessonUrl({ lessonSlug: lesson.slug! });
    }

    return '';
  }

  if (content.__typename === ContentType.ROLE) {
    const role = content as Role;

    return getCollectionUrl(CollectionType.ROLE_BASED, role.slug);
  }

  if (content.__typename === ContentType.TOPIC) {
    const topic = content as Topic;

    return getCollectionUrl(CollectionType.TOPIC, topic.slug);
  }

  if (content.__typename === ContentType.CERTIFICATION) {
    const certification = content as Certification;

    return getCertificationUrl(certification.slug);
  }

  if (content.__typename === ContentType.CERTIFICATION_PREP) {
    const certPrep = content as CertificationPrep;

    return getCollectionUrl(CollectionType.CERT_PREP, certPrep.slug);
  }

  if (content.__typename === ContentType.CONTENT_HUB || content.__typename === ContentType.FLEXIBLE_CONTENT_HUB) {
    const hub = content as ContentHub;

    return getHubUrl(hub.type);
  }

  return '';
};

export const getContentLinkLabel = (content: ContentItem): string => {
  if (content.__typename === ContentType.ASSOCIATED_CONTENT) {
    const associatedContent = content as AssociatedContent;

    return associatedContent.link?.label || 'Learn more';
  }

  return 'Learn more';
};

export const getContentLinkTarget = (content: ContentItem | Resource): LinkTarget | undefined => {
  if (content.__typename === ContentType.ASSOCIATED_CONTENT) {
    const associatedContent = content as AssociatedContent; 

    return associatedContent.link?.target as LinkTarget;
  }

  return '_self';
};

export const getContentAsset = (content: ContentItem): undefined | AtImage => {
  if (content.__typename === ContentType.ASSOCIATED_CONTENT) {
    const associatedContent = content as AssociatedContent;

    return associatedContent.asset;
  }

  if (content.__typename === ContentType.ROLE) {
    const role = content as Role;

    return role.icon;
  }

  return undefined;
};
